/* eslint-disable import/no-anonymous-default-export */
import Cookies from "js-cookie";
import { Action } from "../../../domain/entities/ActionModel";
import { SessionState, StateStatus } from "../../../domain/entities/State";
import { ActionTypes } from "../actions/session";

export const initialState: SessionState = {
  loggedUser: undefined,
  loggedUserArray: undefined,
  loginStatus: StateStatus.INIT,
  loginError: undefined,
  menuVisible: true,
  menuSelected: [null],
  passwordUpdateStatus: StateStatus.INIT,
  passwordUpdate: undefined,
  passwordUpdateError: undefined,
  requestPasswordUpdateStatus: StateStatus.INIT,
  requestPasswordUpdate: undefined,
  requestPasswordUpdateError: undefined,
  currentPDFInstance: undefined,
  currentPDFInstanceStatus: StateStatus.INIT,
  pinSendStatus: StateStatus.INIT,
  pinSendError: null,
  cognitoAuthLoginStatus: StateStatus.INIT,
  cognitoAuthLoginError: undefined,
};

function storeAuthTokens(payload: any): boolean {
  const { IdToken, RefreshToken, AccessToken } = payload;
  if (IdToken && RefreshToken) {
    Cookies.set("id_token", IdToken, { expires: 1, secure: true, sameSite: "Strict" });
    Cookies.set("refresh_token", RefreshToken, { expires: 30, secure: true, sameSite: "Strict" });
    if (AccessToken) {
      Cookies.set("access_token", AccessToken, { expires: 1, secure: true, sameSite: "Strict" });
    }
    try {
      const jwtPayload = JSON.parse(atob(IdToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      if (jwtPayload.email) {
        Cookies.set("auth_username", jwtPayload.email, { expires: 30, secure: true, sameSite: "Strict" });
      }
    } catch { /* ignore decode errors */ }
    return true;
  }
  return false;
}

export default (state = initialState, action: Action<any>): SessionState => {
  switch (action.type) {
    case ActionTypes.LOGIN_COMPLETE: {
      const usersArray = action.payload;
      return {
        ...state,
        loggedUser: usersArray[0], //Sets first elements as default login
        loggedUserArray: usersArray,
        loginStatus: StateStatus.COMPLETE,
        loginError: null,
      };
    }
    case ActionTypes.LOGIN_ERROR: {
      return {
        ...state,
        loginStatus: StateStatus.ERROR,
        loginError: action.payload,
      };
    }
    case ActionTypes.LOGIN_PROCESSING: {
      return {
        ...state,
        loginStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.EXTERNAL_LOGIN_COMPLETE: {
      const usersArray = action.payload;
      return {
        ...state,
        loggedUser: usersArray[0], //Sets first elements as default login
        loggedUserArray: usersArray,
        loginStatus: StateStatus.COMPLETE,
        loginError: null,
      };
    }
    case ActionTypes.EXTERNAL_LOGIN_ERROR: {
      return {
        ...state,
        loginStatus: StateStatus.ERROR,
        loginError: action.payload,
      };
    }
    case ActionTypes.EXTERNAL_LOGIN_PROCESSING: {
      return {
        ...state,
        loginStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.MENU_SELECTED: {
      return {
        ...state,
        menuSelected: [action.payload],
      };
    }
    case ActionTypes.MENU_VISIBLE: {
      return {
        ...state,
        menuVisible: action.payload,
      };
    }
    case ActionTypes.PASSWORD_UPDATE_COMPLETE: {
      return {
        ...state,
        passwordUpdateStatus: StateStatus.COMPLETE,
        passwordUpdate: action.payload,
        passwordUpdateError: null,
      };
    }
    case ActionTypes.PASSWORD_UPDATE_ERROR: {
      return {
        ...state,
        passwordUpdateStatus: StateStatus.ERROR,
        passwordUpdateError: action.payload,
      };
    }
    case ActionTypes.PASSWORD_UPDATE_PROCESSING: {
      return {
        ...state,
        passwordUpdateStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.REQUEST_PASSWORD_UPDATE_COMPLETE: {
      return {
        ...state,
        requestPasswordUpdateStatus: StateStatus.COMPLETE,
        requestPasswordUpdate: action.payload,
        requestPasswordUpdateError: null,
      };
    }
    case ActionTypes.REQUEST_PASSWORD_UPDATE_ERROR: {
      return {
        ...state,
        requestPasswordUpdateStatus: StateStatus.ERROR,
        requestPasswordUpdateError: action.payload,
      };
    }
    case ActionTypes.REQUEST_PASSWORD_UPDATE_PROCESSING: {
      return {
        ...state,
        requestPasswordUpdateStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.REQUEST_PASSWORD_RESTORE: {
      return {
        ...state,
        requestPasswordUpdateStatus: StateStatus.INIT,
        requestPasswordUpdateError: null,
        requestPasswordUpdate: undefined,
      };
    }
    case ActionTypes.CURRENT_PDF_INSTANCE: {
      return {
        ...state,
        currentPDFInstance: action.payload,
        currentPDFInstanceStatus: action.payload
          ? StateStatus.COMPLETE
          : StateStatus.ERROR,
      };
    }
case ActionTypes.SIGNATORY_SEND_PIN_COMPLETE: {
      return {
        ...state,
        pinSendStatus: StateStatus.COMPLETE,
      };
    }
    case ActionTypes.SIGNATORY_SEND_PIN_ERROR: {
      return {
        ...state,
        pinSendStatus: StateStatus.ERROR,
        pinSendError: action.payload,
      };
    }
    case ActionTypes.SIGNATORY_SEND_PIN_PROCESSING: {
      return {
        ...state,
        pinSendStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.COGNITO_AUTH_LOGIN_COMPLETE: {
      if (action.payload.challenge === "NEW_PASSWORD_REQUIRED") {
        return {
          ...state,
          loginError: "NEW_PASSWORD_REQUIRED",
          cognitoAuthLoginError: null,
        };
      }
      if (action.payload.challenge === "SOFTWARE_TOKEN_MFA") {
        return {
          ...state,
          loginError: "SOFTWARE_TOKEN_MFA",
          mfaSession: action.payload.session,
          mfaUsername: action.payload.username,
          cognitoAuthLoginError: null,
        };
      }
      if (storeAuthTokens(action.payload)) {
        return {
          ...state,
          cognitoAuthLoginStatus: StateStatus.COMPLETE,
          cognitoAuthLoginError: null,
        };
      } else {
        return {
          ...state,
          cognitoAuthLoginStatus: StateStatus.ERROR,
          cognitoAuthLoginError: "No tokens received",
        };
      }
    }
    case ActionTypes.MFA_RESPOND_PROCESSING: {
      return { ...state, cognitoAuthLoginStatus: StateStatus.LOADING };
    }
    case ActionTypes.MFA_RESPOND_COMPLETE: {
      if (storeAuthTokens(action.payload)) {
        return {
          ...state,
          cognitoAuthLoginStatus: StateStatus.COMPLETE,
          cognitoAuthLoginError: null,
          mfaSession: undefined,
          mfaUsername: undefined,
        };
      }
      return { ...state, cognitoAuthLoginStatus: StateStatus.ERROR, cognitoAuthLoginError: "No tokens received" };
    }
    case ActionTypes.MFA_RESPOND_ERROR: {
      const errorMsg = action.payload?.message || "";
      return {
        ...state,
        loginError: errorMsg === "MFA_SESSION_EXPIRED" ? "MFA_SESSION_EXPIRED" : "MFA_INVALID_CODE",
        cognitoAuthLoginStatus: StateStatus.ERROR,
        cognitoAuthLoginError: action.payload,
      };
    }
    case ActionTypes.COGNITO_AUTH_LOGIN_ERROR: {
      return {
        ...state,
        loginError: "AUTH_COGNITO_LOGIN",
        cognitoAuthLoginStatus: StateStatus.ERROR,
        cognitoAuthLoginError: action.payload,
      };
    }
    case ActionTypes.COGNITO_AUTH_LOGIN_PROCESSING: {
      return {
        ...state,
        cognitoAuthLoginStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.CLEAR_LOGIN_ERROR: {
      return {
        ...state,
        loginError: null
      };
    }
    case ActionTypes.SET_LOGIN_ERROR: {
      return {
        ...state,
        loginError: action.payload,
      };
    }
    default:
      return state;
  }
};
